import { Grid } from '../../core/Grid.js';
import { splitEntries } from '../stateSerialization.js';
import { Position, Direction, Edge, edgeAdjacentCells } from '../../core/GridGeometry.js';
import { ScoreBreakdown, calculateScore } from '../../core/Scoring.js';
import type { ScoringHighlight } from '../scoringRules.js';

type Quarter = 'NW' | 'NE' | 'SW' | 'SE';

const ALL_QUARTERS: Quarter[] = ['NW', 'NE', 'SW', 'SE'];

// Quarter key: "row,col,quarter"
function quarterKey(row: number, col: number, quarter: Quarter): string {
    return `${row},${col},${quarter}`;
}

// Half-edge key: normalized pair of quarter keys
function halfEdgeKey(r1: number, c1: number, q1: Quarter, r2: number, c2: number, q2: Quarter): string {
    const k1 = quarterKey(r1, c1, q1);
    const k2 = quarterKey(r2, c2, q2);
    // Normalize by sorting lexicographically
    return k1 < k2 ? `${k1},${k2}` : `${k2},${k1}`;
}

// Direction offsets
const DIR_OFFSET: Record<Direction, { dr: number; dc: number }> = {
    'N': { dr: -1, dc: 0 },
    'S': { dr: 1, dc: 0 },
    'E': { dr: 0, dc: 1 },
    'W': { dr: 0, dc: -1 }
};

// Adjacent quarters within a cell (which direction's half-edge separates them)
const WITHIN_CELL_ADJACENT: Array<[Quarter, Quarter, Direction]> = [
    ['NW', 'NE', 'N'],  // N half-edge separates NW from NE
    ['NE', 'SE', 'E'],  // E half-edge separates NE from SE
    ['SW', 'SE', 'S'],  // S half-edge separates SW from SE
    ['NW', 'SW', 'W'],  // W half-edge separates NW from SW
];

// Cross-cell quarter adjacency: going in direction D from quarter Q connects to neighbor's quarter
const CROSS_CELL_QUARTERS: Record<Direction, Record<Quarter, Quarter | null>> = {
    'N': { 'NW': 'SW', 'NE': 'SE', 'SW': null, 'SE': null },
    'S': { 'SW': 'NW', 'SE': 'NE', 'NW': null, 'NE': null },
    'E': { 'NE': 'NW', 'SE': 'SW', 'NW': null, 'SW': null },
    'W': { 'NW': 'NE', 'SW': 'SE', 'NE': null, 'SE': null }
};

// Which quarters are separated by a fuse going in direction D
const FUSE_SEPARATES: Record<Direction, [Quarter, Quarter]> = {
    'N': ['NW', 'NE'],
    'S': ['SW', 'SE'],
    'E': ['NE', 'SE'],
    'W': ['NW', 'SW']
};

export class IgnitionGame {
    private grid: Grid;

    // Half-edges as normalized quarter pairs
    private defaultWalls: Set<string> = new Set();  // Cross-cell half-edges
    private fuseHalfEdges: Set<string> = new Set(); // Within-cell half-edges

    // For rendering: track which fuse segments exist (row, col, direction)
    private fuseSegments: Set<string> = new Set();

    // Digit positions
    private digitPositions: Map<number, Position> = new Map();
    private digitCells: Set<string> = new Set();

    // Computed state
    protected quarterToChamber: Map<string, number> = new Map();
    private chamberIgnitionTime: Map<number, number> = new Map();
    private centerChamberId: number = -1;
    private branchingViolations: Set<string> = new Set();  // positionKeys of cells with >2 fuses
    private selectedDigit: number | null = null;
    private chamberPredecessor: Map<number, number> = new Map();  // Dijkstra predecessor
    // "from->to" → fuse chain cells + endpoint fuse directions
    private chamberEdgeCells: Map<string, { cells: Position[]; fromDir: Direction; toDir: Direction }> = new Map();

    constructor(grid: Grid) {
        this.grid = grid;
        this.initializeDigitPositions();
        this.initializeDefaultWalls();
        this.recalculate();
    }

    private initializeDigitPositions(): void {
        const positions = this.grid.getAllDigitPositions();
        for (const [digit, pos] of positions) {
            this.digitPositions.set(digit, pos);
            this.digitCells.add(`${pos.row},${pos.col}`);
        }
    }

    private initializeDefaultWalls(): void {
        // Create concentric closed squares based on grid size
        // Each wall segment is 2 cross-cell half-edges
        const numRings = Math.floor((this.grid.size - 2) / 2);
        for (let ring = 0; ring < numRings; ring++) {
            const min = ring + 1;
            const max = this.grid.size - 1 - ring;

            // Top wall: between rows (min-1) and min
            for (let col = min; col < max; col++) {
                // Wall between cell (min-1, col) and (min, col)
                // Blocks (min-1,col).SW from (min,col).NW
                // Blocks (min-1,col).SE from (min,col).NE
                this.defaultWalls.add(halfEdgeKey(min - 1, col, 'SW', min, col, 'NW'));
                this.defaultWalls.add(halfEdgeKey(min - 1, col, 'SE', min, col, 'NE'));
            }

            // Bottom wall: between rows (max-1) and max
            for (let col = min; col < max; col++) {
                this.defaultWalls.add(halfEdgeKey(max - 1, col, 'SW', max, col, 'NW'));
                this.defaultWalls.add(halfEdgeKey(max - 1, col, 'SE', max, col, 'NE'));
            }

            // Left wall: between cols (min-1) and min
            for (let row = min; row < max; row++) {
                // Wall between cell (row, min-1) and (row, min)
                // Blocks (row,min-1).NE from (row,min).NW
                // Blocks (row,min-1).SE from (row,min).SW
                this.defaultWalls.add(halfEdgeKey(row, min - 1, 'NE', row, min, 'NW'));
                this.defaultWalls.add(halfEdgeKey(row, min - 1, 'SE', row, min, 'SW'));
            }

            // Right wall: between cols (max-1) and max
            for (let row = min; row < max; row++) {
                this.defaultWalls.add(halfEdgeKey(row, max - 1, 'NE', row, max, 'NW'));
                this.defaultWalls.add(halfEdgeKey(row, max - 1, 'SE', row, max, 'SW'));
            }
        }
    }

    getGrid(): Grid {
        return this.grid;
    }

    getDefaultWalls(): Set<string> {
        return this.defaultWalls;
    }

    getFuseSegments(): Set<string> {
        return this.fuseSegments;
    }

    getDigitPositions(): Map<number, Position> {
        return this.digitPositions;
    }

    getBranchingViolations(): Set<string> {
        return new Set(this.branchingViolations);
    }

    private isDigitCell(row: number, col: number): boolean {
        return this.digitCells.has(`${row},${col}`);
    }

    // Get the direction from a cell's perspective to an edge
    private getDirectionToEdge(cell: Position, edge: Edge): Direction {
        if (edge.orient === 'h') {
            return cell.row < edge.pa.row ? 'S' : 'N';
        } else {
            return cell.col < edge.pa.col ? 'E' : 'W';
        }
    }

    // Check if a fuse exists at an edge
    hasFuse(edge: Edge): boolean {
        const cells = edgeAdjacentCells(edge);
        if (cells.length === 0) return false;
        const cell = cells[0];
        const dir = this.getDirectionToEdge(cell, edge);
        return this.fuseSegments.has(`${cell.row},${cell.col},${dir}`);
    }

    // Toggle a fuse at an edge
    toggleFuse(edge: Edge): boolean {
        // Get adjacent cells (1 for rim, 2 for internal)
        const cells = edgeAdjacentCells(edge);
        if (cells.length === 0) return false;

        // Check if any adjacent cell is a digit cell
        for (const cell of cells) {
            if (this.isDigitCell(cell.row, cell.col)) {
                return false;
            }
        }

        // Build list of half-segments to toggle
        const halfSegs: Array<{ cell: Position; dir: Direction }> = [];
        for (const cell of cells) {
            const dir = this.getDirectionToEdge(cell, edge);
            halfSegs.push({ cell, dir });
        }

        // Check if fuse already exists
        const firstSeg = halfSegs[0];
        const firstSegKey = `${firstSeg.cell.row},${firstSeg.cell.col},${firstSeg.dir}`;
        const fuseExists = this.fuseSegments.has(firstSegKey);

        if (fuseExists) {
            // Remove fuse
            for (const { cell, dir } of halfSegs) {
                const segKey = `${cell.row},${cell.col},${dir}`;
                this.fuseSegments.delete(segKey);

                const [q1, q2] = FUSE_SEPARATES[dir];
                this.fuseHalfEdges.delete(halfEdgeKey(cell.row, cell.col, q1, cell.row, cell.col, q2));
            }
        } else {
            // Add all half-segments
            for (const { cell, dir } of halfSegs) {
                const segKey = `${cell.row},${cell.col},${dir}`;
                this.fuseSegments.add(segKey);

                const [q1, q2] = FUSE_SEPARATES[dir];
                this.fuseHalfEdges.add(halfEdgeKey(cell.row, cell.col, q1, cell.row, cell.col, q2));
            }
        }

        this.recalculate();
        return true;
    }

    private oppositeDir(dir: Direction): Direction {
        const opp: Record<Direction, Direction> = { 'N': 'S', 'S': 'N', 'E': 'W', 'W': 'E' };
        return opp[dir];
    }

    protected recalculate(): void {
        this.computeBranchingViolations();
        this.computeChambers();
        this.onChambersComputed();
        if (this.branchingViolations.size > 0) {
            this.chamberIgnitionTime.clear();
        } else {
            this.computeIgnition();
        }
    }

    protected onChambersComputed(): void {
        // Hook for subclasses (e.g. IgnitionVortex merges gate pair chambers)
    }

    private computeBranchingViolations(): void {
        this.branchingViolations.clear();
        const cellCounts = new Map<string, number>();
        for (const segKey of this.fuseSegments) {
            const [rowStr, colStr] = segKey.split(',');
            const cellKey = `${rowStr},${colStr}`;
            cellCounts.set(cellKey, (cellCounts.get(cellKey) ?? 0) + 1);
        }
        for (const [cellKey, count] of cellCounts) {
            if (count > 2) {
                this.branchingViolations.add(cellKey);
            }
        }
    }

    private computeChambers(): void {
        this.quarterToChamber.clear();

        // All half-edges block chamber connections
        const allHalfEdges = new Set([...this.defaultWalls, ...this.fuseHalfEdges]);

        let nextChamberId = 0;

        for (let row = 0; row < this.grid.size; row++) {
            for (let col = 0; col < this.grid.size; col++) {
                for (const quarter of ALL_QUARTERS) {
                    const qKey = quarterKey(row, col, quarter);
                    if (this.quarterToChamber.has(qKey)) continue;

                    const chamberId = nextChamberId++;
                    this.floodFillQuarters(row, col, quarter, chamberId, allHalfEdges);
                }
            }
        }

        // Center chamber: SE quarter of the cell at (size/2 - 1, size/2 - 1)
        // This is the NW cell of the center 2x2 area
        const centerCell = Math.floor(this.grid.size / 2) - 1;
        const centerQKey = quarterKey(centerCell, centerCell, 'SE');
        this.centerChamberId = this.quarterToChamber.get(centerQKey) ?? -1;
    }

    private floodFillQuarters(
        startRow: number,
        startCol: number,
        startQuarter: Quarter,
        chamberId: number,
        halfEdges: Set<string>
    ): void {
        const queue: Array<{ row: number; col: number; quarter: Quarter }> = [
            { row: startRow, col: startCol, quarter: startQuarter }
        ];
        this.quarterToChamber.set(quarterKey(startRow, startCol, startQuarter), chamberId);

        while (queue.length > 0) {
            const { row, col, quarter } = queue.shift()!;

            // 1. Check adjacent quarters within the same cell
            for (const [q1, q2] of WITHIN_CELL_ADJACENT) {
                let neighbor: Quarter | null = null;
                if (q1 === quarter) neighbor = q2;
                else if (q2 === quarter) neighbor = q1;
                else continue;

                const neighborQKey = quarterKey(row, col, neighbor);
                if (this.quarterToChamber.has(neighborQKey)) continue;

                // Check if blocked by half-edge
                const heKey = halfEdgeKey(row, col, q1, row, col, q2);
                if (halfEdges.has(heKey)) continue;

                this.quarterToChamber.set(neighborQKey, chamberId);
                queue.push({ row, col, quarter: neighbor });
            }

            // 2. Check adjacent quarters in neighboring cells
            for (const dir of ['N', 'E', 'S', 'W'] as Direction[]) {
                const neighborQuarter = CROSS_CELL_QUARTERS[dir][quarter];
                if (neighborQuarter === null) continue;

                const { dr, dc } = DIR_OFFSET[dir];
                const neighborRow = row + dr;
                const neighborCol = col + dc;

                if (neighborRow < 0 || neighborRow >= this.grid.size ||
                    neighborCol < 0 || neighborCol >= this.grid.size) {
                    continue;
                }

                const neighborQKey = quarterKey(neighborRow, neighborCol, neighborQuarter);
                if (this.quarterToChamber.has(neighborQKey)) continue;

                // Check if blocked by cross-cell half-edge
                const heKey = halfEdgeKey(row, col, quarter, neighborRow, neighborCol, neighborQuarter);
                if (halfEdges.has(heKey)) continue;

                this.quarterToChamber.set(neighborQKey, chamberId);
                queue.push({ row: neighborRow, col: neighborCol, quarter: neighborQuarter });
            }
        }
    }

    private computeIgnition(): void {
        this.chamberIgnitionTime.clear();
        this.chamberPredecessor.clear();
        this.chamberEdgeCells.clear();

        if (this.centerChamberId < 0) return;

        // Build chamber graph from fuse connections
        const chamberEdges = new Map<number, Array<{ target: number; length: number }>>();
        this.findFuseConnections(chamberEdges);

        // Dijkstra from center (edges have variable lengths)
        const queue: Array<{ chamberId: number; time: number }> = [
            { chamberId: this.centerChamberId, time: 0 }
        ];
        this.chamberIgnitionTime.set(this.centerChamberId, 0);

        while (queue.length > 0) {
            // Extract minimum-time entry
            let minIdx = 0;
            for (let i = 1; i < queue.length; i++) {
                if (queue[i].time < queue[minIdx].time) minIdx = i;
            }
            const { chamberId, time } = queue[minIdx];
            queue[minIdx] = queue[queue.length - 1];
            queue.pop();

            // Skip if we already found a shorter path
            if (time > this.chamberIgnitionTime.get(chamberId)!) continue;

            const edges = chamberEdges.get(chamberId) || [];
            for (const edge of edges) {
                const newTime = time + edge.length;
                const existing = this.chamberIgnitionTime.get(edge.target);
                if (existing !== undefined && existing <= newTime) continue;

                this.chamberIgnitionTime.set(edge.target, newTime);
                this.chamberPredecessor.set(edge.target, chamberId);
                queue.push({ chamberId: edge.target, time: newTime });
            }
        }
    }

    private findFuseConnections(chamberEdges: Map<number, Array<{ target: number; length: number }>>): void {
        // Group segments by cell to find endpoints (cells with exactly 1 segment)
        const cellSegments = new Map<string, Set<Direction>>();
        for (const segKey of this.fuseSegments) {
            const [rowStr, colStr, dir] = segKey.split(',');
            const cellKey = `${rowStr},${colStr}`;
            if (!cellSegments.has(cellKey)) cellSegments.set(cellKey, new Set());
            cellSegments.get(cellKey)!.add(dir as Direction);
        }

        // Find endpoint cells (cells with exactly 1 segment)
        const endpoints: Array<{ row: number; col: number; dir: Direction }> = [];
        for (const [cellKey, dirs] of cellSegments) {
            if (dirs.size === 1) {
                const [rowStr, colStr] = cellKey.split(',');
                const dir = [...dirs][0];
                endpoints.push({ row: parseInt(rowStr), col: parseInt(colStr), dir });
            }
        }

        // Track processed half-segments (not cells) to avoid backtracking
        const processedSegments = new Set<string>();

        for (const startEndpoint of endpoints) {
            const startSegKey = `${startEndpoint.row},${startEndpoint.col},${startEndpoint.dir}`;
            if (processedSegments.has(startSegKey)) continue;

            // Trace chain from this endpoint
            const { endEndpoint, halfSegments, cells } = this.traceChain(
                startEndpoint, cellSegments, processedSegments
            );

            // Get chambers at both endpoints
            const startChamber = this.getChamberAtEndpoint(startEndpoint);
            const endChamber = this.getChamberAtEndpoint(endEndpoint);

            if (startChamber === null || endChamber === null) continue;
            if (startChamber === endChamber) continue;

            // Length is half-segments / 2 (each cell-to-cell crossing is 2 half-segments)
            const length = Math.floor(halfSegments / 2);

            if (!chamberEdges.has(startChamber)) chamberEdges.set(startChamber, []);
            chamberEdges.get(startChamber)!.push({ target: endChamber, length });

            if (!chamberEdges.has(endChamber)) chamberEdges.set(endChamber, []);
            chamberEdges.get(endChamber)!.push({ target: startChamber, length });

            // Store cell paths + endpoint fuse directions for both directions
            this.chamberEdgeCells.set(`${startChamber}->${endChamber}`, {
                cells,
                fromDir: startEndpoint.dir,
                toDir: endEndpoint!.dir,
            });
            this.chamberEdgeCells.set(`${endChamber}->${startChamber}`, {
                cells: [...cells].reverse(),
                fromDir: endEndpoint!.dir,
                toDir: startEndpoint.dir,
            });
        }
    }

    private traceChain(
        start: { row: number; col: number; dir: Direction },
        cellSegments: Map<string, Set<Direction>>,
        processedSegments: Set<string>
    ): { endEndpoint: { row: number; col: number; dir: Direction } | null; halfSegments: number; cells: Position[] } {
        let row = start.row;
        let col = start.col;
        let dir = start.dir;
        let halfSegments = 1; // Count the starting half-segment
        const cells: Position[] = [{ row, col }];

        // Mark starting segment as processed
        processedSegments.add(`${row},${col},${dir}`);

        while (true) {
            // Move to next cell in direction
            const { dr, dc } = DIR_OFFSET[dir];
            const nextRow = row + dr;
            const nextCol = col + dc;

            // Check bounds
            if (nextRow < 0 || nextRow >= this.grid.size ||
                nextCol < 0 || nextCol >= this.grid.size) {
                // Chain ends at grid boundary
                return { endEndpoint: null, halfSegments, cells };
            }

            const entryDir = this.oppositeDir(dir);
            const entrySegKey = `${nextRow},${nextCol},${entryDir}`;

            // Check if this segment was already processed
            if (processedSegments.has(entrySegKey)) {
                return { endEndpoint: { row, col, dir }, halfSegments, cells };
            }

            const nextDirs = cellSegments.get(`${nextRow},${nextCol}`);

            // Check if fuse continues into next cell
            if (!nextDirs || !nextDirs.has(entryDir)) {
                // Chain ends here (fuse doesn't enter next cell)
                return { endEndpoint: { row, col, dir }, halfSegments, cells };
            }

            // Move to next cell and mark entry segment as processed
            row = nextRow;
            col = nextCol;
            halfSegments++; // Count entering this cell
            processedSegments.add(entrySegKey);
            cells.push({ row, col });

            // Find exit direction (if any)
            let exitDir: Direction | null = null;
            for (const d of nextDirs) {
                if (d !== entryDir) {
                    const exitSegKey = `${row},${col},${d}`;
                    if (!processedSegments.has(exitSegKey)) {
                        exitDir = d;
                        break;
                    }
                }
            }

            if (exitDir === null) {
                // Only entry segment exists (or exit already processed) - this is the other endpoint
                return { endEndpoint: { row, col, dir: entryDir }, halfSegments, cells };
            }

            // Continue through this cell, mark exit segment as processed
            dir = exitDir;
            halfSegments++; // Count exiting this cell
            processedSegments.add(`${row},${col},${dir}`);
        }
    }

    private getChamberAtEndpoint(endpoint: { row: number; col: number; dir: Direction } | null): number | null {
        // The fuse endpoint is off grid (past the edge)
        if (endpoint === null) return null;

        // The endpoint cell has one fuse segment going in `dir`
        // The chamber is on the opposite side - pick any quarter NOT on the fuse's side
        const [q1, q2] = FUSE_SEPARATES[endpoint.dir];

        for (const q of ALL_QUARTERS) {
            if (q !== q1 && q !== q2) {
                const qKey = quarterKey(endpoint.row, endpoint.col, q);
                return this.quarterToChamber.get(qKey) ?? null;
            }
        }
        return null;
    }

    // Get ignition time for a cell - only if all 4 quarters agree
    getIgnitionTime(pos: Position): number | null {
        const times = new Set<number>();
        for (const q of ALL_QUARTERS) {
            const qKey = quarterKey(pos.row, pos.col, q);
            const chamberId = this.quarterToChamber.get(qKey);
            if (chamberId === undefined) return null;

            const time = this.chamberIgnitionTime.get(chamberId);
            if (time === undefined) return null;

            times.add(time);
        }

        // Only return time if all quarters have the same time
        if (times.size === 1) {
            return [...times][0];
        }
        return null;
    }

    // Get ignition time for any quarter of a cell (for digit checking)
    getAnyQuarterIgnitionTime(pos: Position): number | null {
        const qKey = quarterKey(pos.row, pos.col, 'NW');
        const chamberId = this.quarterToChamber.get(qKey);
        if (chamberId === undefined) return null;
        return this.chamberIgnitionTime.get(chamberId) ?? null;
    }

    /**
     * Ignition time of the chamber on the un-fused side of a fuse endpoint cell
     * (the chamber the fuse pokes into from this tip). Null if unreached by fire.
     * Used by the renderer to tell fire sources from sinks along a fuse.
     */
    getEndpointIgnitionTime(pos: Position, fuseDir: Direction): number | null {
        const [q1, q2] = FUSE_SEPARATES[fuseDir];
        for (const q of ALL_QUARTERS) {
            if (q !== q1 && q !== q2) {
                const chamberId = this.quarterToChamber.get(quarterKey(pos.row, pos.col, q));
                if (chamberId === undefined) return null;
                return this.chamberIgnitionTime.get(chamberId) ?? null;
            }
        }
        return null;
    }

    /**
     * Whether a fuse half-segment functions as a wall: the two quarters it
     * separates lie in different chambers, so the fuse is actively splitting a
     * chamber here. A tip poking into a chamber doesn't qualify — its two sides
     * reconnect around the open end, so they share a chamber.
     */
    isFuseWall(row: number, col: number, dir: Direction): boolean {
        const [q1, q2] = FUSE_SEPARATES[dir];
        const c1 = this.quarterToChamber.get(quarterKey(row, col, q1));
        const c2 = this.quarterToChamber.get(quarterKey(row, col, q2));
        return c1 !== undefined && c2 !== undefined && c1 !== c2;
    }

    checkWin(): boolean {
        if (this.branchingViolations.size > 0) return false;

        let previousTime = -1;
        for (let digit = this.grid.minDigit; digit <= this.grid.maxDigit; digit++) {
            const pos = this.digitPositions.get(digit);
            if (!pos) return false;

            // Digits can't be split, so any quarter works
            const time = this.getAnyQuarterIgnitionTime(pos);
            if (time === null) return false;
            if (time <= previousTime) return false;

            previousTime = time;
        }
        return true;
    }

    getScore(): ScoreBreakdown | null {
        if (!this.checkWin()) return null;

        // A = fuse edges (each edge stored twice, so divide by 2)
        const fuseEdges = Math.floor(this.fuseSegments.size / 2);

        // B = time spread (last digit time - first digit time)
        const posMin = this.digitPositions.get(this.grid.minDigit);
        const posMax = this.digitPositions.get(this.grid.maxDigit);
        if (!posMin || !posMax) return null;

        const timeMin = this.getAnyQuarterIgnitionTime(posMin);
        const timeMax = this.getAnyQuarterIgnitionTime(posMax);
        if (timeMin === null || timeMax === null) return null;

        const timeSpread = timeMax - timeMin;

        return calculateScore(fuseEdges, timeSpread);
    }

    /** Scoring-rules overlay: time spread (secondary) as an arc between the min/max
     *  digit. The fuse (primary) is redrawn in amber by the renderer. */
    getScoringHighlight(): ScoringHighlight {
        const from = this.grid.getDigitPosition(this.grid.minDigit);
        const to = this.grid.getDigitPosition(this.grid.maxDigit);
        if (!from || !to) return {};
        const fromValue = Math.round(this.getAnyQuarterIgnitionTime(from) ?? 0);
        const toValue = Math.round(this.getAnyQuarterIgnitionTime(to) ?? 0);
        return { spreadArc: { from, to, fromValue, toValue, primary: false, badgeSize: 'sm' } };
    }

    getCorrectDigits(): number[] {
        const correct: number[] = [];
        let previousTime = -1;

        for (let digit = this.grid.minDigit; digit <= this.grid.maxDigit; digit++) {
            const pos = this.digitPositions.get(digit);
            if (!pos) break;

            const time = this.getAnyQuarterIgnitionTime(pos);
            if (time === null) break;
            if (time <= previousTime) break;

            correct.push(digit);
            previousTime = time;
        }
        return correct;
    }

    getSelectedDigit(): number | null {
        return this.selectedDigit;
    }

    toggleSelectedDigit(digit: number): void {
        this.selectedDigit = this.selectedDigit === digit ? null : digit;
    }

    clearSelectedDigit(): void {
        this.selectedDigit = null;
    }

    /**
     * Reconstruct the shortest ignition path from center to the selected digit.
     * Alternates between intra-chamber quarter paths and fuse chain cell paths.
     * Each point has an optional quarter: null = cell center, Quarter = quarter center.
     */
    getIgnitionPath(): Array<{ row: number; col: number; quarter: Quarter | null }> | null {
        if (this.selectedDigit === null) return null;

        const pos = this.digitPositions.get(this.selectedDigit);
        if (!pos) return null;

        // Find digit's chamber
        const digitQKey = quarterKey(pos.row, pos.col, 'NW');
        const digitChamber = this.quarterToChamber.get(digitQKey);
        if (digitChamber === undefined) return null;
        if (!this.chamberIgnitionTime.has(digitChamber)) return null;

        const centerCell = Math.floor(this.grid.size / 2) - 1;

        // If digit is in the center chamber, just quarter-BFS directly
        if (digitChamber === this.centerChamberId) {
            return this.quarterBFS(centerCell, centerCell, 'SE', pos.row, pos.col);
        }

        // Walk predecessors from digit chamber back to center
        const chamberChain: number[] = [digitChamber];
        let current = digitChamber;
        while (current !== this.centerChamberId) {
            const pred = this.chamberPredecessor.get(current);
            if (pred === undefined) return null;
            chamberChain.push(pred);
            current = pred;
        }
        chamberChain.reverse(); // center -> ... -> digitChamber

        const path: Array<{ row: number; col: number; quarter: Quarter | null }> = [];

        // Current position for intra-chamber BFS starts at center quarter
        let curRow = centerCell;
        let curCol = centerCell;
        let curQuarter: Quarter = 'SE';

        for (let i = 0; i < chamberChain.length - 1; i++) {
            const conn = this.chamberEdgeCells.get(`${chamberChain[i]}->${chamberChain[i + 1]}`);
            if (!conn) return null;

            // Intra-chamber: quarter BFS from current position to fuse chain start
            const fuseStart = conn.cells[0];
            const fuseStartQuarter = this.getQuarterInChamber(conn.fromDir);
            if (!fuseStartQuarter) return null;

            const chamberPath = this.quarterBFS(curRow, curCol, curQuarter, fuseStart.row, fuseStart.col);
            if (chamberPath) {
                for (const pt of chamberPath) path.push(pt);
            }

            // Fuse chain: cell centers
            for (const cell of conn.cells) {
                path.push({ row: cell.row, col: cell.col, quarter: null });
            }

            // Update current position to end of fuse chain
            const fuseEnd = conn.cells[conn.cells.length - 1];
            const fuseEndQuarter = this.getQuarterInChamber(conn.toDir);
            if (!fuseEndQuarter) return null;
            curRow = fuseEnd.row;
            curCol = fuseEnd.col;
            curQuarter = fuseEndQuarter;
        }

        // Final intra-chamber: from last fuse end to digit
        const finalPath = this.quarterBFS(curRow, curCol, curQuarter, pos.row, pos.col);
        if (finalPath) {
            for (const pt of finalPath) path.push(pt);
        }

        return path;
    }

    /** A quarter on the open side of a fuse running in `fuseDir` — i.e. one of the two
     *  the fuse does not wall off. Which of the two doesn't matter: both sit in the same
     *  chamber, so either is a valid BFS start. Depends only on the direction, not the cell. */
    private getQuarterInChamber(fuseDir: Direction): Quarter | null {
        const [q1, q2] = FUSE_SEPARATES[fuseDir];
        for (const q of ALL_QUARTERS) {
            if (q !== q1 && q !== q2) return q;
        }
        return null;
    }

    /**
     * BFS through connected quarters (within a chamber) from a start quarter
     * to any quarter of the target cell. Returns quarter-center path points.
     */
    protected quarterBFS(
        fromRow: number, fromCol: number, fromQuarter: Quarter,
        toRow: number, toCol: number
    ): Array<{ row: number; col: number; quarter: Quarter | null }> | null {
        const targetCell = `${toRow},${toCol}`;
        const startQKey = quarterKey(fromRow, fromCol, fromQuarter);

        if (`${fromRow},${fromCol}` === targetCell) return [];

        const allHalfEdges = new Set([...this.defaultWalls, ...this.fuseHalfEdges]);
        const predecessor = new Map<string, string>();
        const queue: Array<{ row: number; col: number; quarter: Quarter }> = [
            { row: fromRow, col: fromCol, quarter: fromQuarter }
        ];
        predecessor.set(startQKey, '');

        let foundQKey: string | null = null;

        while (queue.length > 0) {
            const { row, col, quarter } = queue.shift()!;
            const currentQKey = quarterKey(row, col, quarter);

            if (`${row},${col}` === targetCell) {
                foundQKey = currentQKey;
                break;
            }

            // Within-cell adjacencies
            for (const [q1, q2] of WITHIN_CELL_ADJACENT) {
                let neighbor: Quarter | null = null;
                if (q1 === quarter) neighbor = q2;
                else if (q2 === quarter) neighbor = q1;
                else continue;

                const neighborQKey = quarterKey(row, col, neighbor);
                if (predecessor.has(neighborQKey)) continue;

                const heKey = halfEdgeKey(row, col, q1, row, col, q2);
                if (allHalfEdges.has(heKey)) continue;

                predecessor.set(neighborQKey, currentQKey);
                queue.push({ row, col, quarter: neighbor });
            }

            // Cross-cell adjacencies
            for (const dir of ['N', 'E', 'S', 'W'] as Direction[]) {
                const neighborQuarter = CROSS_CELL_QUARTERS[dir][quarter];
                if (neighborQuarter === null) continue;

                const { dr, dc } = DIR_OFFSET[dir];
                const nr = row + dr;
                const nc = col + dc;
                if (nr < 0 || nr >= this.grid.size || nc < 0 || nc >= this.grid.size) continue;

                const neighborQKey = quarterKey(nr, nc, neighborQuarter);
                if (predecessor.has(neighborQKey)) continue;

                const heKey = halfEdgeKey(row, col, quarter, nr, nc, neighborQuarter);
                if (allHalfEdges.has(heKey)) continue;

                predecessor.set(neighborQKey, currentQKey);
                queue.push({ row: nr, col: nc, quarter: neighborQuarter });
            }

            // Gate teleportation adjacencies (no wall check needed)
            for (const neighbor of this.getGateQuarterNeighbors(row, col, quarter)) {
                const neighborQKey = quarterKey(neighbor.row, neighbor.col, neighbor.quarter);
                if (predecessor.has(neighborQKey)) continue;
                predecessor.set(neighborQKey, currentQKey);
                queue.push(neighbor);
            }
        }

        if (foundQKey === null) return null;

        // Reconstruct path (skip start point, caller already positioned there)
        const result: Array<{ row: number; col: number; quarter: Quarter | null }> = [];
        let cur = foundQKey;
        while (cur !== '' && cur !== startQKey) {
            const parts = cur.split(',');
            result.push({ row: parseInt(parts[0]), col: parseInt(parts[1]), quarter: parts[2] as Quarter });
            cur = predecessor.get(cur)!;
        }
        result.reverse();
        return result;
    }

    /** Additional quarter neighbors via gate teleportation. Override in subclasses. */
    protected getGateQuarterNeighbors(
        _row: number, _col: number, _quarter: Quarter
    ): Array<{ row: number; col: number; quarter: Quarter }> {
        return [];
    }

    isStarted(): boolean {
        return this.fuseSegments.size > 0;
    }

    reset(): void {
        this.fuseSegments.clear();
        this.fuseHalfEdges.clear();
        this.selectedDigit = null;
        this.recalculate();
    }

    /**
     * Jumpstart a solution: run a fuse from the central fire source out to the
     * first digit's chamber so it ignites. A BFS lays a fuse path of cells from
     * a center cell to a cell next to digit 1 (trying each neighbour so the one
     * in digit 1's chamber is found), verified so digit 1 gets an ignition time.
     */
    autoStart(): boolean {
        if (this.isStarted()) return false;

        const min = this.grid.minDigit;
        const d1 = this.digitPositions.get(min);
        if (!d1) return false;

        const size = this.grid.size;
        const cc = Math.floor(size / 2) - 1;
        const centers: Position[] = [
            { row: cc, col: cc }, { row: cc, col: cc + 1 },
            { row: cc + 1, col: cc }, { row: cc + 1, col: cc + 1 },
        ];
        const inGrid = (r: number, c: number) => r >= 0 && r < size && c >= 0 && c < size;
        const isDigit = (r: number, c: number) => this.isDigitCell(r, c);

        const targets: Position[] = [];
        for (const [dr, dc] of [[-1, 0], [1, 0], [0, -1], [0, 1]] as Array<[number, number]>) {
            const r = d1.row + dr, c = d1.col + dc;
            if (inGrid(r, c) && !isDigit(r, c)) targets.push({ row: r, col: c });
        }

        for (const target of targets) {
            for (const start of centers) {
                if (isDigit(start.row, start.col)) continue;
                const path = this.bfsFusePath(start, target);
                if (!path) continue;

                this.reset();
                let ok = true;
                for (let i = 0; i + 1 < path.length; i++) {
                    const a = path[i], b = path[i + 1];
                    const dir: Direction = b.row < a.row ? 'N' : b.row > a.row ? 'S' : b.col < a.col ? 'W' : 'E';
                    const edge = this.grid.getEdgeFromPosition(a, dir);
                    if (!this.toggleFuse(edge) || !this.hasFuse(edge)) { ok = false; break; }
                }
                if (!ok) continue;
                if (this.branchingViolations.size === 0 && this.getCorrectDigits().length >= 1) return true;
            }
        }

        this.reset();
        return false;
    }

    /** Shortest cell path (4-connected) from start to target avoiding digit cells. */
    private bfsFusePath(start: Position, target: Position): Position[] | null {
        const size = this.grid.size;
        const key = (p: Position) => `${p.row},${p.col}`;
        const prev = new Map<string, Position | null>();
        prev.set(key(start), null);
        const queue: Position[] = [start];

        while (queue.length > 0) {
            const cur = queue.shift()!;
            if (key(cur) === key(target)) {
                const path: Position[] = [];
                let c: Position | null = cur;
                while (c) { path.push(c); c = prev.get(key(c)) ?? null; }
                return path.length >= 2 ? path.reverse() : null;
            }
            for (const [dr, dc] of [[-1, 0], [1, 0], [0, -1], [0, 1]] as Array<[number, number]>) {
                const r = cur.row + dr, c = cur.col + dc;
                if (r < 0 || r >= size || c < 0 || c >= size) continue;
                if (this.isDigitCell(r, c)) continue;
                const np = { row: r, col: c };
                if (prev.has(key(np))) continue;
                prev.set(key(np), cur);
                queue.push(np);
            }
        }
        return null;
    }

    getStatus(): string {
        const boldRed = (t: string) => `<span style="color:#e94560;font-weight:bold">${t}</span>`;
        const boldAmber = (t: string) => `<span style="color:#fbbf24;font-weight:bold">${t}</span>`;

        if (this.checkWin()) {
            return 'You win! All digits ignite in strictly increasing order!';
        }

        // A cell with 3+ fuses breaks the no-branching rule outright — a red border
        // on the board, and a red Invalid here.
        if (this.branchingViolations.size > 0) {
            const n = this.branchingViolations.size;
            return `${boldRed('Invalid')}: ${n} cell${n > 1 ? 's have' : ' has'} branching fuses. ` +
                `Each cell can have at most 2.`;
        }

        const correct = this.getCorrectDigits();
        if (this.fuseSegments.size === 0) {
            return 'Click cell edges to place fuses. Fire spreads from center.';
        }

        // `nextDigit` is the first digit not in the correct run, so it is always
        // either not yet reached or reached out of order — never reached-and-ordered
        // (that would have extended the run), and never past the last digit (that is
        // a win, handled above). So these two cases are exhaustive for a real board.
        const nextDigit = correct.length + this.grid.minDigit;
        const pos = nextDigit <= this.grid.maxDigit ? this.digitPositions.get(nextDigit) : undefined;
        if (pos) {
            const time = this.getAnyQuarterIgnitionTime(pos);
            const prevDigit = nextDigit - 1;
            const prevTime = correct.length > 0
                ? this.getAnyQuarterIgnitionTime(this.digitPositions.get(prevDigit)!)
                : -1;

            // Reached, but too early: an ordering problem, shown amber to match the
            // digit's amber badge.
            if (time !== null && prevTime !== null && time <= prevTime) {
                return `${boldAmber('Warning')}: digit ${nextDigit} (t=${time}) must ignite ` +
                    `after digit ${prevDigit} (t=${prevTime}).`;
            }
            // Not yet connected to the fire — an in-progress state, not an error.
            return `Digit ${nextDigit} is not reached yet. Connect fuses to reach it.`;
        }

        // Unreachable for a well-formed grid (every digit present); a defensive tail.
        return 'Click cell edges to place fuses. Fire spreads from center.';
    }

    protected get gs() { return this.grid.serializer; }

    /**
     * Serialize game state to string format.
     * Format: "2021,3040" - comma-separated point-based edge serializations.
     * Each edge is 4 chars: col+row of each endpoint point.
     */
    serialize(): string {
        const seen = new Set<string>();
        const entries: string[] = [];

        for (const segKey of this.fuseSegments) {
            const [rowStr, colStr, dir] = segKey.split(',');
            const row = parseInt(rowStr, 10);
            const col = parseInt(colStr, 10);

            const edge = this.grid.getEdgeFromPosition({ row, col }, dir as Direction);
            const serialized = this.gs.serializeEdge(edge);
            if (!seen.has(serialized)) {
                seen.add(serialized);
                entries.push(serialized);
            }
        }
        return entries.join('');
    }

    /** Add the fuse half-segments for a point-edge string (e.g. "2021"). */
    private addPointEdgeFuse(edgeStr: string): void {
        const edge = this.grid.deserializeEdge(edgeStr);
        if (!edge) return;
        for (const cell of edgeAdjacentCells(edge)) {
            if (this.isDigitCell(cell.row, cell.col)) continue;
            const dir = this.getDirectionToEdge(cell, edge);
            this.addFuseSegment(cell.row, cell.col, dir);
        }
    }

    /**
     * Deserialize game state. Canonical form is compact point-edges ("20213040");
     * comma-separated point edges (the solver's output) are also accepted.
     */
    deserialize(serialized: string): void {
        this.reset();
        if (!serialized) return;

        for (const entry of splitEntries(serialized, 4)) {
            this.addPointEdgeFuse(entry);
        }

        this.recalculate();
    }

    private addFuseSegment(row: number, col: number, dir: Direction): void {
        const segKey = `${row},${col},${dir}`;
        if (this.fuseSegments.has(segKey)) return;

        this.fuseSegments.add(segKey);
        const [q1, q2] = FUSE_SEPARATES[dir];
        this.fuseHalfEdges.add(halfEdgeKey(row, col, q1, row, col, q2));
    }
}
